import { useCallback, useState } from 'react';

import { ACTION_IDLE, ACTION_SUBMITTING, ACTION_DONE, stateFor } from '../recordActionStates';

import { approvalActionErrorMessage } from 'Helpers/errors';

/**
 * Submits a status change from the work-section detail pop-up.
 *
 * SHAPED LIKE `useApprovalAction` AND READING THE SAME `stateFor`, because the
 * two answer the same questions: was my input wrong, or is what I am looking at
 * stale. Only the request differs — one field, `status_id`, against the
 * record's own status endpoint.
 *
 * THE SERVER'S REFRESHED PAYLOAD IS HANDED BACK, not a bare success. The pop-up
 * renders what the server declares rather than patching its own copy, so a
 * status write that also changes what the user may do next is reflected without
 * a second fetch. `onUpdated` receives it.
 *
 * A 403 HERE MEANS THE MODULE'S OWN MANAGE ABILITY IS MISSING, not that the
 * control should have been hidden — the server already told the pop-up whether
 * to offer it via `can_update_status`, and this state is what a race against a
 * revoked permission looks like. It is terminal, so the caller refreshes.
 */
export default function useWorkAction({ urlTemplate, detailTypePlaceholder, idPlaceholder, onUpdated } = {}) {
    const [state, setState] = useState(ACTION_IDLE);
    const [message, setMessage] = useState('');

    const reset = useCallback(() => {
        setState(ACTION_IDLE);
        setMessage('');
    }, []);

    const submit = useCallback(
        (detailType, id, statusId) => {
            // Same guard as the read: the template comes from a `data-*`
            // attribute, and `.replace` on a missing one throws where there is
            // no error boundary to catch it.
            if (!urlTemplate) {
                return Promise.resolve();
            }

            setState(ACTION_SUBMITTING);
            setMessage('');

            const url = urlTemplate.replace(detailTypePlaceholder, detailType).replace(idPlaceholder, id);

            return axios
                .patch(url, { status_id: statusId })
                .then(({ data }) => {
                    setState(ACTION_DONE);
                    onUpdated?.(data?.data ?? null);
                })
                .catch((error) => {
                    setState(stateFor(error));
                    setMessage(approvalActionErrorMessage(error, $t('This action could not be completed.')));
                });
        },
        [urlTemplate, detailTypePlaceholder, idPlaceholder, onUpdated],
    );

    return { state, message, submit, reset, submitting: state === ACTION_SUBMITTING };
}
